import { useEffect, useRef } from 'react';

const isPageVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Calls `callback` every `intervalMs` while the page is visible.
 *
 * - Pauses while the tab / installed app is in the background.
 * - Runs right away when the page becomes visible again, comes back online or
 *   regains focus (at most once per `minGapMs`, so these events don't stack).
 * - If `callback` returns a promise, no new run starts until it has settled.
 * - A falsy `intervalMs` or `enabled: false` stops polling.
 *
 * Polling on a phone keeps the radio awake, so every poll should use this hook.
 */
export function useVisibleInterval(
  callback,
  intervalMs,
  { enabled = true, minGapMs = 5000 } = {},
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let timer = null;
    let inFlight = false;
    let disposed = false;
    // the caller does its own initial load, so count mounting as a run
    let lastRun = Date.now();

    const run = async () => {
      if (inFlight || disposed) return;
      inFlight = true;
      lastRun = Date.now();
      try {
        await callbackRef.current?.();
      } catch (error) {
        console.error(error);
      } finally {
        inFlight = false;
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      if (disposed || !isPageVisible()) return;
      timer = setTimeout(async () => {
        await run();
        schedule();
      }, intervalMs);
    };

    const handleResume = () => {
      if (!isPageVisible()) {
        clearTimeout(timer);
        return;
      }
      if (Date.now() - lastRun >= minGapMs) run();
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('online', handleResume);
    window.addEventListener('focus', handleResume);

    return () => {
      disposed = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('online', handleResume);
      window.removeEventListener('focus', handleResume);
    };
  }, [intervalMs, enabled, minGapMs]);
}
